// VIP Token/Certificate Generation Service
// Generates VIP token certificates with user data overlay, QR code, and photo

import QRCode from 'qrcode';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  collection,
  addDoc,
  serverTimestamp,
  query,
  orderBy,
  limit,
  getDocs,
  where,
  doc,
  updateDoc,
  increment,
  getDoc,
  setDoc,
  runTransaction,
  writeBatch
} from 'firebase/firestore';
import { storage, db } from '../config/firebase';
import { COLLECTIONS } from '../utils/constants';

/**
 * VIP Token Generator Configuration
 */
const TOKEN_CONFIG = {
  // Canvas dimensions (Mobile card style - vertical)
  width: 1080,
  height: 1920,
  
  // Layout positions (matching reference design exactly)
  layout: {
    // Card container with padding
    cardPadding: 40,
    cardRadius: 60,
    
    // Logo at top
    logo: { x: 540, y: 170, width: 480, height: 140 },
    
    // Circular photo with golden ring
    photo: { x: 540, y: 400, radius: 135 },
    photoRingRadius: 150,
    
    // User name
    name: { x: 540, y: 650 },
    
    // VIP Customer badge
    badge: { x: 540, y: 745 },
    
    // Subtitle
    subtitle: { x: 540, y: 820 },
    
    // Status row (Token, Active, Lifetime)
    statusRow: {
      x: 540,
      y: 945,
      width: 960,
      height: 148,
      radius: 44,
      paddingX: 48,
      paddingY: 26,
      segmentSpacing: 24,
      segmentRadius: 30
    },
    
    // Information section container
    infoSectionTop: 1035,
    infoSectionWidth: 960,
    infoSectionRadius: 58,
    infoSectionPaddingX: 78,
    infoSectionPaddingTop: 58,
    infoSectionPaddingBottom: 52,
    infoRowHeight: 120,
    infoRowSpacing: 20,
    infoRowHorizontalInset: 26,
    infoRowCornerRadius: 34,
    infoRowIconOffset: 68,
    infoRowTextOffset: 172,
    infoIconSize: 52,
    
    // Social media icons
    socialIconSize: 52
  },
  
  // Typography
  fonts: {
    logo: 'bold 46px Arial, sans-serif',
    name: 'bold 68px Arial, sans-serif',
    badge: 'bold 34px Arial, sans-serif',
    subtitle: '30px Arial, sans-serif',
    statusLabel: '600 26px Arial, sans-serif',
    statusValue: 'bold 30px Arial, sans-serif',
    infoText: '34px Arial, sans-serif',
    infoLabel: 'bold 30px Arial, sans-serif'
  },
  
  // Colors (matching reference design exactly)
  colors: {
    // Purple gradient background (deeper purple like reference)
    purpleGradientStart: '#7c3aed',
    purpleGradientMid: '#9333ea',
    purpleGradientEnd: '#a855f7',
    
    // Text colors
    white: '#ffffff',
    darkPurple: '#581c87',
    lightPurple: '#e9d5ff',
    
    // Badge colors (golden yellow)
    badgeGold: '#fbbf24',
    badgeGoldDark: '#f59e0b',
    
    // Status colors
    statusBg: '#e9d5ff',
    activeGreen: '#10b981',
    
    // Info row background (light purple semi-transparent)
    infoBg: '#e9d5ff',
    infoBgAlpha: 'rgba(233, 213, 255, 0.75)',
    infoSectionBg: 'rgba(255, 255, 255, 0.06)',
    
    // Social media colors
    youtubeRed: '#ff0000',
    facebookBlue: '#1877f2',
    instagramGradient: ['#f09433', '#e6683c', '#dc2743', '#cc2366', '#bc1888']
  }
};

const extractSocialNameFromLink = (link) => {
  if (!link) return '';
  const trimmed = link.trim();
  if (!trimmed) return '';

  const ensureProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const url = new URL(ensureProtocol);
    const pathParts = url.pathname.split('/').filter(Boolean);
    if (pathParts.length > 0) {
      return pathParts[pathParts.length - 1].replace(/[-_]/g, ' ');
    }
    return url.hostname.replace(/^www\./i, '');
  } catch (error) {
    return trimmed.replace(/^https?:\/\//i, '').replace(/^www\./i, '');
  }
};

const addSocialInfoRow = (infoRows, icon, link, handle, fallbackLabel) => {
  const cleanedHandle = handle?.trim();
  const derived = cleanedHandle || extractSocialNameFromLink(link);
  if (!derived) return;

  infoRows.push({ icon, text: derived, profileName: null });
};

const TOKEN_NUMBER_PREFIX = 'VIP';
const TOKEN_NUMBER_PADDING = 4;
const DEFAULT_SEQUENCE_START = 1;
const MIN_SEQUENCE_VALUE = 1;
const TOKEN_SEQUENCE_DOC_ID = 'vipTokenSequence';
const TOKEN_SEQUENCE_SCHEMA_VERSION = 3;

const getTokenSequenceDocRef = () => doc(db, COLLECTIONS.SETTINGS, TOKEN_SEQUENCE_DOC_ID);

const formatTokenNumber = (numericValue) =>
  `${TOKEN_NUMBER_PREFIX}${String(Math.max(MIN_SEQUENCE_VALUE, numericValue)).padStart(TOKEN_NUMBER_PADDING, '0')}`;

const resetTokenSequenceDoc = async (sequenceDocRef) => {
  await setDoc(sequenceDocRef, {
    nextTokenNumeric: DEFAULT_SEQUENCE_START,
    schemaVersion: TOKEN_SEQUENCE_SCHEMA_VERSION,
    updatedAt: serverTimestamp()
  });
  return DEFAULT_SEQUENCE_START;
};

const ensureTokenSequenceDoc = async () => {
  const sequenceDocRef = getTokenSequenceDocRef();
  const snapshot = await getDoc(sequenceDocRef);

  if (!snapshot.exists()) {
    return resetTokenSequenceDoc(sequenceDocRef);
  }

  const data = snapshot.data() || {};
  const docSchemaVersion = data.schemaVersion || 1;

  if (docSchemaVersion < TOKEN_SEQUENCE_SCHEMA_VERSION) {
    return resetTokenSequenceDoc(sequenceDocRef);
  }

  const storedValue = data.nextTokenNumeric;

  if (typeof storedValue !== 'number' || Number.isNaN(storedValue) || storedValue < MIN_SEQUENCE_VALUE) {
    return resetTokenSequenceDoc(sequenceDocRef);
  }

  return storedValue;
};

const performTokenSequenceMigrationIfNeeded = async () => {
  const sequenceDocRef = getTokenSequenceDocRef();
  const snapshot = await getDoc(sequenceDocRef);
  const data = snapshot.exists() ? snapshot.data() : {};

  if (data.sequenceMigrationVersion === TOKEN_SEQUENCE_SCHEMA_VERSION) {
    return data.nextTokenNumeric || DEFAULT_SEQUENCE_START;
  }

  const tokensQuery = query(
    collection(db, COLLECTIONS.VIP_TOKENS),
    orderBy('generatedAt', 'asc')
  );

  const tokensSnapshot = await getDocs(tokensQuery);

  if (tokensSnapshot.empty) {
    await updateDoc(sequenceDocRef, {
      nextTokenNumeric: DEFAULT_SEQUENCE_START,
      schemaVersion: TOKEN_SEQUENCE_SCHEMA_VERSION,
      sequenceMigrationVersion: TOKEN_SEQUENCE_SCHEMA_VERSION,
      updatedAt: serverTimestamp()
    }).catch(async () => {
      await setDoc(sequenceDocRef, {
        nextTokenNumeric: DEFAULT_SEQUENCE_START,
        schemaVersion: TOKEN_SEQUENCE_SCHEMA_VERSION,
        sequenceMigrationVersion: TOKEN_SEQUENCE_SCHEMA_VERSION,
        updatedAt: serverTimestamp()
      });
    });
    return DEFAULT_SEQUENCE_START;
  }

  let counter = MIN_SEQUENCE_VALUE;
  let batch = writeBatch(db);
  let operations = 0;

  for (const docSnap of tokensSnapshot.docs) {
    batch.update(docSnap.ref, {
      tokenNumber: formatTokenNumber(counter),
      sequenceVersion: TOKEN_SEQUENCE_SCHEMA_VERSION,
      sequenceMigratedAt: serverTimestamp()
    });

    counter += 1;
    operations += 1;

    if (operations >= 400) {
      await batch.commit();
      batch = writeBatch(db);
      operations = 0;
    }
  }

  if (operations > 0) {
    await batch.commit();
  }

  await updateDoc(sequenceDocRef, {
    nextTokenNumeric: counter,
    schemaVersion: TOKEN_SEQUENCE_SCHEMA_VERSION,
    sequenceMigrationVersion: TOKEN_SEQUENCE_SCHEMA_VERSION,
    updatedAt: serverTimestamp()
  });

  return counter;
};

const getNextSequentialTokenNumber = async () => {
  const sequenceDocRef = getTokenSequenceDocRef();
  await ensureTokenSequenceDoc();
  await performTokenSequenceMigrationIfNeeded();

  const nextNumericValue = await runTransaction(db, async (transaction) => {
    const sequenceSnapshot = await transaction.get(sequenceDocRef);

    if (!sequenceSnapshot.exists()) {
      throw new Error('TOKEN_SEQUENCE_NOT_INITIALIZED');
    }

    const data = sequenceSnapshot.data() || {};
    let currentNext = data.nextTokenNumeric;

    if (typeof currentNext !== 'number' || Number.isNaN(currentNext) || currentNext < MIN_SEQUENCE_VALUE) {
      currentNext = DEFAULT_SEQUENCE_START;
    }

    transaction.update(sequenceDocRef, {
      nextTokenNumeric: currentNext + 1,
      schemaVersion: TOKEN_SEQUENCE_SCHEMA_VERSION,
      updatedAt: serverTimestamp()
    });

    return currentNext;
  });

  return formatTokenNumber(nextNumericValue);
};

/**
 * Load and prepare an image
 */
const loadImage = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
};

/**
 * Generate QR Code as data URL
 */
const generateQRCode = async (data) => {
  try {
    return await QRCode.toDataURL(data, {
      width: TOKEN_CONFIG.layout.qrCode.size,
      margin: 1,
      color: {
        dark: TOKEN_CONFIG.colors.primary,
        light: '#ffffff'
      }
    });
  } catch (error) {
    console.error('QR Code generation error:', error);
    throw error;
  }
};

/**
 * Draw rounded rectangle
 */
const drawRoundedRect = (ctx, x, y, width, height, radius) => {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
};

const drawPlaceholderAvatar = (ctx, centerX, centerY, radius) => {
  // Draw golden ring (outer)
  const ringGradient = ctx.createLinearGradient(
    centerX - TOKEN_CONFIG.layout.photoRingRadius,
    centerY - TOKEN_CONFIG.layout.photoRingRadius,
    centerX + TOKEN_CONFIG.layout.photoRingRadius,
    centerY + TOKEN_CONFIG.layout.photoRingRadius
  );
  ringGradient.addColorStop(0, TOKEN_CONFIG.colors.badgeGold);
  ringGradient.addColorStop(0.5, '#fcd34d');
  ringGradient.addColorStop(1, TOKEN_CONFIG.colors.badgeGoldDark);

  ctx.fillStyle = ringGradient;
  ctx.beginPath();
  ctx.arc(centerX, centerY, TOKEN_CONFIG.layout.photoRingRadius, 0, Math.PI * 2);
  ctx.fill();

  // Draw white inner ring
  ctx.fillStyle = TOKEN_CONFIG.colors.white;
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius + 8, 0, Math.PI * 2);
  ctx.fill();

  // Draw placeholder circle
  const placeholderGradient = ctx.createRadialGradient(centerX, centerY, radius * 0.2, centerX, centerY, radius);
  placeholderGradient.addColorStop(0, '#f5f3ff');
  placeholderGradient.addColorStop(1, '#ddd6fe');

  ctx.fillStyle = placeholderGradient;
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
  ctx.fill();

  // Draw placeholder icon
  ctx.fillStyle = '#7c3aed';
  ctx.font = '120px Arial';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText('👤', centerX, centerY);
};

/**
 * Draw user photo in circular frame with golden ring
 */
const drawUserPhoto = async (ctx, photoUrl, centerX, centerY, radius) => {
  if (!photoUrl) {
    drawPlaceholderAvatar(ctx, centerX, centerY, radius);
    return;
  }

  try {
    const img = await loadImage(photoUrl);
    
    // Draw golden ring (outer)
    const ringGradient = ctx.createLinearGradient(
      centerX - TOKEN_CONFIG.layout.photoRingRadius,
      centerY - TOKEN_CONFIG.layout.photoRingRadius,
      centerX + TOKEN_CONFIG.layout.photoRingRadius,
      centerY + TOKEN_CONFIG.layout.photoRingRadius
    );
    ringGradient.addColorStop(0, TOKEN_CONFIG.colors.badgeGold);
    ringGradient.addColorStop(0.5, '#fcd34d');
    ringGradient.addColorStop(1, TOKEN_CONFIG.colors.badgeGoldDark);

    ctx.fillStyle = ringGradient;
    ctx.beginPath();
    ctx.arc(centerX, centerY, TOKEN_CONFIG.layout.photoRingRadius, 0, Math.PI * 2);
    ctx.fill();

    // Draw white inner ring
    ctx.fillStyle = TOKEN_CONFIG.colors.white;
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius + 8, 0, Math.PI * 2);
    ctx.fill();

    // Save context
    ctx.save();
    
    // Create circular clipping path
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();
    
    // Calculate aspect ratio fit for circle
    const imgAspect = img.width / img.height;
    let drawWidth, drawHeight, drawX, drawY;
    
    if (imgAspect > 1) {
      drawHeight = radius * 2;
      drawWidth = drawHeight * imgAspect;
      drawX = centerX - drawWidth / 2;
      drawY = centerY - radius;
    } else {
      drawWidth = radius * 2;
      drawHeight = drawWidth / imgAspect;
      drawX = centerX - radius;
      drawY = centerY - drawHeight / 2;
    }
    
    // Draw image
    ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
    
    // Restore context
    ctx.restore();
    
  } catch (error) {
    console.warn('Falling back to placeholder avatar due to photo load issue:', error?.message || error);
    drawPlaceholderAvatar(ctx, centerX, centerY, radius);
  }
};

/**
 * Draw decorative bokeh circles on purple background
 */
const drawBokehCircles = (ctx) => {
  const circles = [
    { x: 180, y: 180, radius: 110, opacity: 0.18 },
    { x: 900, y: 250, radius: 130, opacity: 0.15 },
    { x: 140, y: 480, radius: 90, opacity: 0.12 },
    { x: 920, y: 680, radius: 100, opacity: 0.16 },
    { x: 280, y: 880, radius: 75, opacity: 0.13 },
    { x: 780, y: 1080, radius: 95, opacity: 0.17 },
    { x: 160, y: 1280, radius: 105, opacity: 0.14 },
    { x: 880, y: 1480, radius: 85, opacity: 0.12 },
    { x: 380, y: 1680, radius: 90, opacity: 0.15 }
  ];
  
  circles.forEach(circle => {
    ctx.fillStyle = `rgba(255, 255, 255, ${circle.opacity})`;
    ctx.beginPath();
    ctx.arc(circle.x, circle.y, circle.radius, 0, Math.PI * 2);
    ctx.fill();
  });
};

/**
 * Draw sparkle effects
 */
const drawSparkles = (ctx) => {
  const sparkles = [
    { x: 240, y: 340, size: 3.5 },
    { x: 820, y: 440, size: 4.5 },
    { x: 340, y: 640, size: 3 },
    { x: 720, y: 840, size: 4 },
    { x: 440, y: 1040, size: 3.5 },
    { x: 620, y: 1240, size: 3 },
    { x: 290, y: 1440, size: 4.5 },
    { x: 770, y: 1640, size: 3.5 }
  ];
  
  ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
  sparkles.forEach(sparkle => {
    ctx.beginPath();
    ctx.arc(sparkle.x, sparkle.y, sparkle.size, 0, Math.PI * 2);
    ctx.fill();
  });
};

/**
 * Draw the main card background with purple gradient
 */
const drawCardBackground = (ctx) => {
  const { width, height } = TOKEN_CONFIG;
  const { cardPadding, cardRadius } = TOKEN_CONFIG.layout;
  
  // Draw outer glow/shadow
  ctx.shadowColor = 'rgba(139, 92, 246, 0.5)';
  ctx.shadowBlur = 40;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 20;
  
  // Draw card with rounded corners and white border
  ctx.fillStyle = TOKEN_CONFIG.colors.white;
  drawRoundedRect(ctx, cardPadding - 8, cardPadding - 8, width - (cardPadding * 2) + 16, height - (cardPadding * 2) + 16, cardRadius + 8);
  ctx.fill();
  
  // Reset shadow
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
  
  // Draw purple gradient background
  const gradient = ctx.createLinearGradient(0, 0, 0, height);
  gradient.addColorStop(0, TOKEN_CONFIG.colors.purpleGradientStart);
  gradient.addColorStop(0.5, TOKEN_CONFIG.colors.purpleGradientMid);
  gradient.addColorStop(1, TOKEN_CONFIG.colors.purpleGradientEnd);
  
  ctx.fillStyle = gradient;
  drawRoundedRect(ctx, cardPadding, cardPadding, width - (cardPadding * 2), height - (cardPadding * 2), cardRadius);
  ctx.fill();
  
  // Draw bokeh circles
  drawBokehCircles(ctx);
  
  // Draw sparkles
  drawSparkles(ctx);
};

/**
 * Draw Offer Lifetime logo at the top
 */
const drawLogo = async (ctx) => {
  const logoLayout = TOKEN_CONFIG.layout.logo;

  // Draw subtle highlight behind logo for better visibility
  const highlightGradient = ctx.createRadialGradient(
    logoLayout.x,
    logoLayout.y,
    0,
    logoLayout.x,
    logoLayout.y,
    220
  );
  highlightGradient.addColorStop(0, 'rgba(255, 255, 255, 0.35)');
  highlightGradient.addColorStop(0.6, 'rgba(255, 255, 255, 0.15)');
  highlightGradient.addColorStop(1, 'rgba(255, 255, 255, 0)');

  ctx.fillStyle = highlightGradient;
  ctx.beginPath();
  ctx.arc(logoLayout.x, logoLayout.y, 220, 0, Math.PI * 2);
  ctx.fill();

  try {
    const logo = await loadImage('/images/images/logo.png');
    
    // Calculate aspect ratio fit
    const imgAspect = logo.width / logo.height;
    const boxAspect = logoLayout.width / logoLayout.height;
    
    let drawWidth, drawHeight, drawX, drawY;
    
    if (imgAspect > boxAspect) {
      drawWidth = logoLayout.width;
      drawHeight = logoLayout.width / imgAspect;
      drawX = logoLayout.x - drawWidth / 2;
      drawY = logoLayout.y - drawHeight / 2;
    } else {
      drawHeight = logoLayout.height;
      drawWidth = logoLayout.height * imgAspect;
      drawX = logoLayout.x - drawWidth / 2;
      drawY = logoLayout.y - drawHeight / 2;
    }
    
    ctx.drawImage(logo, drawX, drawY, drawWidth, drawHeight);
  } catch (error) {
    console.error('Error loading logo:', error);
    // Fallback to text
    ctx.fillStyle = TOKEN_CONFIG.colors.white;
    ctx.font = TOKEN_CONFIG.fonts.logo;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('OFFER LIFETIME', logoLayout.x, logoLayout.y);
  }
};

/**
 * Draw VIP CUSTOMER badge with golden gradient
 */
const drawVIPBadge = (ctx, x, y) => {
  const badgeWidth = 310;
  const badgeHeight = 62;
  const badgeRadius = 31;
  
  // Draw golden gradient badge
  const gradient = ctx.createLinearGradient(x - badgeWidth/2, y - badgeHeight/2, x + badgeWidth/2, y + badgeHeight/2);
  gradient.addColorStop(0, TOKEN_CONFIG.colors.badgeGold);
  gradient.addColorStop(0.5, '#fcd34d');
  gradient.addColorStop(1, TOKEN_CONFIG.colors.badgeGoldDark);
  
  ctx.fillStyle = gradient;
  drawRoundedRect(ctx, x - badgeWidth/2, y - badgeHeight/2, badgeWidth, badgeHeight, badgeRadius);
  ctx.fill();
  
  // Draw badge text
  ctx.fillStyle = TOKEN_CONFIG.colors.darkPurple;
  ctx.font = TOKEN_CONFIG.fonts.badge;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('VIP CUSTOMER', x, y);
};

/**
 * Draw subtitle text
 */
const drawSubtitle = (ctx, x, y) => {
  ctx.fillStyle = TOKEN_CONFIG.colors.white;
  ctx.font = TOKEN_CONFIG.fonts.subtitle;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('Lifetime VIP Member • Offer Lifetime', x, y);
};

/**
 * Draw status row (Token, Active, Lifetime)
 */
const drawStatusRow = (ctx, tokenNumber) => {
  const {
    x,
    y,
    width,
    height,
    radius,
    paddingX,
    paddingY,
    segmentSpacing,
    segmentRadius
  } = TOKEN_CONFIG.layout.statusRow;
  
  const startX = x - width / 2;
  const startY = y - height / 2;
  
  // Draw background container
  ctx.fillStyle = TOKEN_CONFIG.colors.statusBg;
  drawRoundedRect(ctx, startX, startY, width, height, radius);
  ctx.fill();
  
  // Calculate inner segment dimensions
  const innerWidth = width - paddingX * 2;
  const innerHeight = height - paddingY * 2;
  const segmentWidth = (innerWidth - segmentSpacing * 2) / 3;
  const firstSegmentX = startX + paddingX;
  const segmentY = startY + paddingY;
  
  const sections = [
    {
      label: 'Token',
      value: tokenNumber,
      customValueDraw: null
    },
    {
      label: 'Active',
      value: 'Active',
      customValueDraw: (centerX, centerY) => {
        // Draw green dot + text
        ctx.fillStyle = TOKEN_CONFIG.colors.activeGreen;
        ctx.beginPath();
        ctx.arc(centerX - 70, centerY, 10, 0, Math.PI * 2);
        ctx.fill();
        
        ctx.fillStyle = TOKEN_CONFIG.colors.darkPurple;
        ctx.font = TOKEN_CONFIG.fonts.statusValue;
        ctx.textAlign = 'left';
        ctx.fillText('Active', centerX - 50, centerY);
      }
    },
    {
      label: 'Lifetime',
      value: 'Lifetime',
      customValueDraw: null
    }
  ];
  
  sections.forEach((section, index) => {
    const sectionX = firstSegmentX + index * (segmentWidth + segmentSpacing);
    
    // Draw segment card
    ctx.fillStyle = 'rgba(255, 255, 255, 0.98)';
    drawRoundedRect(ctx, sectionX, segmentY, segmentWidth, innerHeight, segmentRadius);
    ctx.fill();
    
    const centerX = sectionX + segmentWidth / 2;
    const labelY = segmentY + innerHeight * 0.35;
    const valueY = segmentY + innerHeight * 0.72;
    
    // Draw label
    ctx.fillStyle = TOKEN_CONFIG.colors.darkPurple;
    ctx.font = TOKEN_CONFIG.fonts.statusLabel;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(section.label, centerX, labelY);
    
    // Draw value / custom content
    if (section.customValueDraw) {
      section.customValueDraw(centerX, valueY);
    } else {
      ctx.fillStyle = TOKEN_CONFIG.colors.darkPurple;
      ctx.font = TOKEN_CONFIG.fonts.statusValue;
      ctx.fillText(section.value, centerX, valueY);
    }
  });
};

/**
 * Draw email icon (envelope)
 */
const drawEmailIcon = (ctx, x, y, size) => {
  ctx.fillStyle = TOKEN_CONFIG.colors.darkPurple;
  
  // Envelope rectangle
  ctx.fillRect(x - size/2, y - size/3, size, size * 0.66);
  
  // Envelope flap
  ctx.beginPath();
  ctx.moveTo(x - size/2, y - size/3);
  ctx.lineTo(x, y + size/6);
  ctx.lineTo(x + size/2, y - size/3);
  ctx.strokeStyle = TOKEN_CONFIG.colors.white;
  ctx.lineWidth = 3;
  ctx.stroke();
};

/**
 * Draw home/address icon
 */
const drawHomeIcon = (ctx, x, y, size) => {
  ctx.fillStyle = TOKEN_CONFIG.colors.darkPurple;
  
  // House roof (triangle)
  ctx.beginPath();
  ctx.moveTo(x, y - size/2);
  ctx.lineTo(x + size/2, y);
  ctx.lineTo(x - size/2, y);
  ctx.closePath();
  ctx.fill();
  
  // House body (rectangle)
  ctx.fillRect(x - size/3, y, size * 0.66, size/2);
};

/**
 * Draw location/pin icon
 */
const drawLocationIcon = (ctx, x, y, size) => {
  ctx.fillStyle = TOKEN_CONFIG.colors.darkPurple;
  
  // Location pin shape
  ctx.beginPath();
  ctx.arc(x, y - size/6, size/3, 0, Math.PI * 2);
  ctx.fill();
  
  // Pin bottom triangle
  ctx.beginPath();
  ctx.moveTo(x - size/4, y);
  ctx.lineTo(x, y + size/2);
  ctx.lineTo(x + size/4, y);
  ctx.closePath();
  ctx.fill();
};

/**
 * Draw YouTube icon
 */
const drawYouTubeIcon = (ctx, x, y, size) => {
  // Red rounded rectangle
  ctx.fillStyle = TOKEN_CONFIG.colors.youtubeRed;
  drawRoundedRect(ctx, x - size/2, y - size/3, size, size * 0.66, size/8);
  ctx.fill();
  
  // White play triangle
  ctx.fillStyle = TOKEN_CONFIG.colors.white;
  ctx.beginPath();
  ctx.moveTo(x - size/6, y - size/6);
  ctx.lineTo(x + size/4, y);
  ctx.lineTo(x - size/6, y + size/6);
  ctx.closePath();
  ctx.fill();
};

/**
 * Draw Facebook icon
 */
const drawFacebookIcon = (ctx, x, y, size) => {
  // Blue circle
  ctx.fillStyle = TOKEN_CONFIG.colors.facebookBlue;
  ctx.beginPath();
  ctx.arc(x, y, size/2, 0, Math.PI * 2);
  ctx.fill();
  
  // White 'f'
  ctx.fillStyle = TOKEN_CONFIG.colors.white;
  ctx.font = `bold ${size * 0.8}px Arial`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('f', x + 2, y);
};

/**
 * Draw Instagram icon
 */
const drawInstagramIcon = (ctx, x, y, size) => {
  // Gradient rounded square
  const gradient = ctx.createLinearGradient(x - size/2, y + size/2, x + size/2, y - size/2);
  gradient.addColorStop(0, '#f09433');
  gradient.addColorStop(0.25, '#e6683c');
  gradient.addColorStop(0.5, '#dc2743');
  gradient.addColorStop(0.75, '#cc2366');
  gradient.addColorStop(1, '#bc1888');
  
  ctx.fillStyle = gradient;
  drawRoundedRect(ctx, x - size/2, y - size/2, size, size, size/6);
  ctx.fill();
  
  // White camera circle
  ctx.strokeStyle = TOKEN_CONFIG.colors.white;
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.arc(x, y, size/4, 0, Math.PI * 2);
  ctx.stroke();
  
  // Small circle (camera lens reflection)
  ctx.fillStyle = TOKEN_CONFIG.colors.white;
  ctx.beginPath();
  ctx.arc(x + size/4, y - size/4, size/12, 0, Math.PI * 2);
  ctx.fill();
};

/**
 * Draw information row with icon and text (supports profile name + URL and multi-line text)
 */
const drawInfoRow = (ctx, icon, text, index, layout, profileName = null) => {
  const {
    infoRowCornerRadius,
    infoRowIconOffset,
    infoRowTextOffset
  } = TOKEN_CONFIG.layout;

  const startX = layout.rowStartX;
  const rowWidth = layout.rowWidth;
  const rowHeight = layout.rowHeight;
  const startY = layout.rowStartY + (index * (rowHeight + layout.rowSpacing));
  
  // Draw background
  ctx.fillStyle = TOKEN_CONFIG.colors.infoBgAlpha;
  drawRoundedRect(ctx, startX, startY, rowWidth, rowHeight, infoRowCornerRadius);
  ctx.fill();
  
  // Draw icon
  const iconOffset = infoRowIconOffset;
  const iconX = startX + iconOffset;
  const iconY = startY + rowHeight / 2;
  
  switch(icon) {
    case 'email':
      drawEmailIcon(ctx, iconX, iconY, 52);
      break;
    case 'home':
      drawHomeIcon(ctx, iconX, iconY, 52);
      break;
    case 'location':
      drawLocationIcon(ctx, iconX, iconY, 52);
      break;
    case 'youtube':
      drawYouTubeIcon(ctx, iconX, iconY, 52);
      break;
    case 'facebook':
      drawFacebookIcon(ctx, iconX, iconY, 52);
      break;
    case 'instagram':
      drawInstagramIcon(ctx, iconX, iconY, 52);
      break;
  }
  
  const textOffset = infoRowTextOffset;
  const textStart = startX + textOffset;
  const maxTextWidth = rowWidth - textOffset - 32;
  
  // If profileName is provided (for social media), show profile name and URL
  if (profileName) {
    ctx.fillStyle = TOKEN_CONFIG.colors.darkPurple;
    ctx.font = 'bold 32px Arial, sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    
    // Draw profile name on top
    let displayProfileName = profileName;
    if (ctx.measureText(profileName).width > maxTextWidth) {
      while (ctx.measureText(displayProfileName + '...').width > maxTextWidth && displayProfileName.length > 0) {
        displayProfileName = displayProfileName.slice(0, -1);
      }
      displayProfileName += '...';
    }
    ctx.fillText(displayProfileName, textStart, startY + rowHeight * 0.35);
    
    // Draw URL below
    ctx.font = '28px Arial, sans-serif';
    ctx.fillStyle = 'rgba(88, 28, 135, 0.7)';
    let displayURL = text;
    if (ctx.measureText(text).width > maxTextWidth) {
      while (ctx.measureText(displayURL + '...').width > maxTextWidth && displayURL.length > 0) {
        displayURL = displayURL.slice(0, -1);
      }
      displayURL += '...';
    }
    ctx.fillText(displayURL, textStart, startY + rowHeight * 0.68);
  } else {
    // Regular single-line text
    ctx.fillStyle = TOKEN_CONFIG.colors.darkPurple;
    ctx.font = TOKEN_CONFIG.fonts.infoText;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    
    // Truncate text if too long
    let displayText = text;
    if (ctx.measureText(text).width > maxTextWidth) {
      while (ctx.measureText(displayText + '...').width > maxTextWidth && displayText.length > 0) {
        displayText = displayText.slice(0, -1);
      }
      displayText += '...';
    }
    
    ctx.fillText(displayText, textStart, iconY);
  }
};

/**
 * Draw background container for information section
 * Returns positioning info for the rows
 */
const drawInfoSectionContainer = (ctx, rowsCount) => {
  const {
    infoSectionTop,
    infoSectionWidth,
    infoSectionRadius,
    infoSectionPaddingX,
    infoSectionPaddingTop,
    infoSectionPaddingBottom,
    infoRowHeight,
    infoRowSpacing
  } = TOKEN_CONFIG.layout;
  
  const totalRowsHeight = (rowsCount * infoRowHeight) + ((rowsCount - 1) * infoRowSpacing);
  const sectionHeight = infoSectionPaddingTop + totalRowsHeight + infoSectionPaddingBottom;
  const sectionStartX = 540 - (infoSectionWidth / 2);
  
  // Draw translucent container
  const gradient = ctx.createLinearGradient(
    sectionStartX,
    infoSectionTop,
    sectionStartX,
    infoSectionTop + sectionHeight
  );
  gradient.addColorStop(0, 'rgba(255, 255, 255, 0.08)');
  gradient.addColorStop(1, TOKEN_CONFIG.colors.infoSectionBg);
  
  ctx.fillStyle = gradient;
  drawRoundedRect(
    ctx,
    sectionStartX,
    infoSectionTop,
    infoSectionWidth,
    sectionHeight,
    infoSectionRadius
  );
  ctx.fill();
  
  return {
    rowStartX: sectionStartX + infoSectionPaddingX,
    rowStartY: infoSectionTop + infoSectionPaddingTop,
    rowWidth: infoSectionWidth - (infoSectionPaddingX * 2),
    rowHeight: infoRowHeight,
    rowSpacing: infoRowSpacing
  };
};

const drawFooterContact = (ctx) => {
  const { width, height } = TOKEN_CONFIG;
  const contactY = height - 140;
  const websiteY = height - 90;

  ctx.save();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
  ctx.font = 'bold 38px Arial, sans-serif';
  ctx.fillText('offerlifetime.com', width / 2, contactY);

  ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
  ctx.font = '600 32px Arial, sans-serif';
  ctx.fillText('Customer Care +91 8818001860', width / 2, websiteY);

  ctx.restore();
};

/**
 * Get or generate token number for a user (one token number per user)
 * If user already has a token, return existing token number
 * If new user, generate next sequential number
 */
const getOrGenerateTokenNumber = async (userId) => {
  try {
    // First, check if this user already has a token
    const userTokensQuery = query(
      collection(db, COLLECTIONS.VIP_TOKENS),
      where('userId', '==', userId),
      limit(1)
    );
    
    const userTokenSnapshot = await getDocs(userTokensQuery);
    
    // If user already has a token, return the existing token number
    if (!userTokenSnapshot.empty) {
      const existingToken = userTokenSnapshot.docs[0].data();
      console.log(`User ${userId} already has token: ${existingToken.tokenNumber}`);
      return existingToken.tokenNumber;
    }
    
    const newTokenNumber = await getNextSequentialTokenNumber();
    console.log(`Generated sequential token for user ${userId}: ${newTokenNumber}`);
    return newTokenNumber;
  } catch (error) {
    console.error('Error getting/generating token number:', error);
    throw new Error(`Failed to generate sequential token number: ${error.message}`);
  }
};

/**
 * Main function to generate VIP Token Certificate
 */
export const generateVipToken = async (userData, tokenNumber) => {
  try {
    console.log('Starting VIP token generation for user:', userData);
    
    let resolvedTokenNumber = tokenNumber;
    if (!resolvedTokenNumber) {
      const probableUserId = userData?.userId || userData?.uid || userData?.id;
      if (probableUserId) {
        console.log('Getting token number for userId:', probableUserId);
        resolvedTokenNumber = await getOrGenerateTokenNumber(probableUserId);
      } else {
        resolvedTokenNumber = await getNextSequentialTokenNumber();
        console.warn('User ID missing during token generation; using sequential fallback:', resolvedTokenNumber);
      }
    }
    
    console.log('Resolved token number:', resolvedTokenNumber);

    // Create canvas
    const canvas = document.createElement('canvas');
    canvas.width = TOKEN_CONFIG.width;
    canvas.height = TOKEN_CONFIG.height;
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }
    
    // Enable high-quality rendering
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    
    // 1. Draw light gray background (for outer glow effect)
    ctx.fillStyle = '#f3f4f6';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // 2. Draw main card background with purple gradient and bokeh
    drawCardBackground(ctx);
    
    // 3. Draw Offer Lifetime logo at top
    try {
      await drawLogo(ctx);
      console.log('Logo drawn successfully');
    } catch (logoError) {
      console.error('Error drawing logo:', logoError);
      // Continue without logo
    }
    
    // 4. Draw user photo with golden ring
    const photoLayout = TOKEN_CONFIG.layout.photo;
    try {
      await drawUserPhoto(
        ctx,
        userData.profileImage || userData.photoURL || null,
        photoLayout.x,
        photoLayout.y,
        photoLayout.radius
      );
      console.log('User photo drawn successfully');
    } catch (photoError) {
      console.error('Error drawing user photo:', photoError);
      // Continue without photo
    }
    
    // 5. Draw user name
    ctx.fillStyle = TOKEN_CONFIG.colors.white;
    ctx.font = TOKEN_CONFIG.fonts.name;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const userName = (userData.name || 'USER NAME').toUpperCase();
    ctx.fillText(userName, TOKEN_CONFIG.layout.name.x, TOKEN_CONFIG.layout.name.y);
    
    // 6. Draw VIP CUSTOMER badge
    drawVIPBadge(ctx, TOKEN_CONFIG.layout.badge.x, TOKEN_CONFIG.layout.badge.y);
    
    // 7. Draw subtitle
    drawSubtitle(ctx, TOKEN_CONFIG.layout.subtitle.x, TOKEN_CONFIG.layout.subtitle.y);
    
    // 8. Draw status row (Token, Active, Lifetime)
    drawStatusRow(ctx, resolvedTokenNumber);
    
    // 9. Draw information section (grouped container with rows)
    const infoRows = [];

    // Add address if available
    if (userData.address) {
      infoRows.push({ icon: 'home', text: userData.address, profileName: null });
    }
    
    // Add location (country, state, city) if available
    const locationParts = [];
    if (userData.country) locationParts.push(userData.country);
    if (userData.state) locationParts.push(userData.state);
    if (userData.city) locationParts.push(userData.city);
    
    if (locationParts.length > 0) {
      infoRows.push({ icon: 'location', text: locationParts.join(', '), profileName: null });
    }
    
    // Add social media with profile names or handles
    addSocialInfoRow(
      infoRows,
      'youtube',
      userData.youtube || userData.youtubeUrl || userData.youtubeLink,
      userData.youtubeName || userData.youtubeProfile,
      'YouTube Channel'
    );
    
    addSocialInfoRow(
      infoRows,
      'facebook',
      userData.facebook || userData.facebookUrl || userData.facebookLink,
      userData.facebookName || userData.facebookProfile,
      'Facebook Profile'
    );
    
    addSocialInfoRow(
      infoRows,
      'instagram',
      userData.instagram || userData.instagramUrl || userData.instagramLink,
      userData.instagramName || userData.instagramProfile,
      'Instagram Profile'
    );

    const infoLayout = drawInfoSectionContainer(ctx, infoRows.length);
    
    infoRows.forEach((row, index) => {
      drawInfoRow(ctx, row.icon, row.text, index, infoLayout, row.profileName);
    });

    drawFooterContact(ctx);
    
    console.log('All drawing complete, converting to blob...');
    
    // 10. Convert canvas to blob and return with tokenNumber
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) {
          console.log('Blob created successfully, size:', blob.size);
          resolve({ blob, canvas, tokenNumber: resolvedTokenNumber });
        } else {
          console.error('Failed to create blob from canvas');
          reject(new Error('Failed to create image blob'));
        }
      }, 'image/png', 1.0);
    });
    
  } catch (error) {
    console.error('VIP Token generation error:', error);
    console.error('Error stack:', error.stack);
    console.error('User data at error:', userData);
    throw error;
  }
};

/**
 * Download generated token as PNG file
 */
export const downloadVIPToken = (canvas, userData) => {
  const link = document.createElement('a');
  const filename = `VIP_Token_${userData.name?.replace(/\s+/g, '_')}_${userData.id}_${Date.now()}.png`;
  link.download = filename;
  link.href = canvas.toDataURL('image/png', 1.0);
  link.click();
};

/**
 * Upload token to Firebase Storage
 */
export const uploadTokenToStorage = async (blob, userData) => {
  try {
    const filename = `vip-tokens/${userData.id}_${Date.now()}.png`;
    const storageRef = ref(storage, filename);
    
    const snapshot = await uploadBytes(storageRef, blob, {
      contentType: 'image/png',
      customMetadata: {
        userId: userData.id,
        userName: userData.name,
        generatedAt: new Date().toISOString()
      }
    });
    
    const downloadURL = await getDownloadURL(snapshot.ref);
    return downloadURL;
    
  } catch (error) {
    console.error('Upload to storage error:', error);
    throw error;
  }
};

/**
 * Save token generation record to Firestore
 */
export const saveTokenGenerationRecord = async (userData, adminEmail, tokenNumber, downloadURL = null) => {
  try {
    const userDocId = userData.id || userData.userId || userData.uid;
    if (!userDocId) {
      throw new Error('Unable to determine user id for token record');
    }

    // Prevent duplicate token creation
    const existingQuery = query(
      collection(db, COLLECTIONS.VIP_TOKENS),
      where('userId', '==', userDocId),
      limit(1)
    );
    const existingSnapshot = await getDocs(existingQuery);
    if (!existingSnapshot.empty) {
      throw new Error('TOKEN_ALREADY_EXISTS');
    }

    const record = {
      userId: userDocId,
      userName: userData.name,
      userEmail: userData.email,
      tokenNumber: tokenNumber, // Store token number for sequential tracking
      generatedBy: adminEmail,
      generatedAt: serverTimestamp(),
      downloadURL: downloadURL,
      tokenType: 'VIP_CERTIFICATE',
      status: 'active',
      sequenceVersion: TOKEN_SEQUENCE_SCHEMA_VERSION
    };
    
    const docRef = await addDoc(collection(db, COLLECTIONS.VIP_TOKENS), record);

    try {
      await updateDoc(
        doc(db, COLLECTIONS.USERS, userDocId),
        {
          totalTokens: increment(1)
        }
      );
    } catch (incrementError) {
      console.warn('Failed to increment user totalTokens:', incrementError);
    }

    return docRef.id;
    
  } catch (error) {
    console.error('Save generation record error:', error);
    throw error;
  }
};

/**
 * Complete VIP Token Generation Workflow
 * This is the main function to call from the UI
 */
export const generateAndDownloadVIPToken = async (userData, adminEmail, options = {}) => {
  const { uploadToStorage = false, saveRecord = true, tokenNumber: providedTokenNumber } = options;
  
  try {
    console.log('=== Starting generateAndDownloadVIPToken ===');
    console.log('User data:', userData);
    console.log('Admin email:', adminEmail);
    console.log('Options:', options);
    
    // Step 1: Generate token image (returns blob, canvas, and tokenNumber)
    console.log('Step 1: Generating token image...');
    const { blob, canvas, tokenNumber } = await generateVipToken(userData, providedTokenNumber);
    console.log('Token image generated successfully, tokenNumber:', tokenNumber);
    
    // Step 2: Download locally
    console.log('Step 2: Downloading token locally...');
    downloadVIPToken(canvas, userData);
    console.log('Token downloaded successfully');
    
    let downloadURL = null;
    
    // Step 3: Upload to Firebase Storage (optional)
    if (uploadToStorage) {
      console.log('Step 3: Uploading to Firebase Storage...');
      downloadURL = await uploadTokenToStorage(blob, userData);
      console.log('Upload successful, URL:', downloadURL);
    }
    
    // Step 4: Save generation record with token number
    if (saveRecord) {
      console.log('Step 4: Saving token record to Firestore...');
      const recordId = await saveTokenGenerationRecord(userData, adminEmail, tokenNumber, downloadURL);
      console.log('Token record saved successfully, ID:', recordId);
    }
    
    return {
      success: true,
      downloadURL,
      tokenNumber,
      message: 'VIP Token generated successfully!'
    };
    
  } catch (error) {
    console.error('=== Complete token generation error ===');
    console.error('Error:', error);
    console.error('Error message:', error?.message);
    console.error('Error stack:', error?.stack);
    console.error('Error code:', error?.code);
    
    const rawMessage = error?.message || 'UNKNOWN_ERROR';
    const errorCode = rawMessage === 'TOKEN_ALREADY_EXISTS' ? 'TOKEN_ALREADY_EXISTS' : (error?.code || rawMessage);
    const friendlyMessage = errorCode === 'TOKEN_ALREADY_EXISTS'
      ? 'You already have a VIP token assigned to your account.'
      : `Failed to generate VIP token: ${error?.message || 'Unknown error'}. Please try again.`;
    return {
      success: false,
      errorCode,
      error: error.message,
      message: friendlyMessage
    };
  }
};
